import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { cellAt, dayLabel } from '@apps/web/features/coverage/model/cell_model';
import {
    CELL_STATE_LABELS,
    type CoverageRow,
    DAY_INDEXES,
    DAY_PARTS,
    EMPLOYMENT_TYPE_LABELS,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import { CELL_STATE_CLASSES } from '@apps/web/features/coverage/ui/availability_cell_styles';
import { cn } from '@apps/web/lib/utils';

export function AvailabilityDetailDialog({
    row,
    workWeekStartDay,
    onClose,
}: {
    readonly row: CoverageRow | null;
    readonly workWeekStartDay: WorkWeekStartDay;
    readonly onClose: () => void;
}) {
    const locationParts = row
        ? [row.city, row.postal_code, row.office_name].filter(Boolean)
        : [];
    // A caregiver with no availability rows can still have scheduled
    // shifts; show the matrix whenever any cell carries information.
    const hasAnyKnownCell =
        row?.cells.some((cell) => cell.state !== 'unknown') ?? false;
    return (
        <Dialog
            open={row !== null}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
        >
            {/* ph-no-capture: dialog is portaled outside the page's
                ph-no-capture root and shows the caregiver's name. */}
            <DialogContent className="ph-no-capture max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{row?.name}</DialogTitle>
                    <DialogDescription>
                        {[
                            locationParts.join(' · '),
                            row?.employment_type
                                ? (EMPLOYMENT_TYPE_LABELS[
                                      row.employment_type
                                  ] ?? row.employment_type)
                                : null,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </DialogDescription>
                </DialogHeader>
                {row !== null && hasAnyKnownCell ? (
                    <div className="overflow-x-auto">
                        <table className="w-full border-separate border-spacing-1">
                            <thead>
                                <tr>
                                    <th
                                        scope="col"
                                        className="text-muted-foreground p-1 text-left text-xs font-semibold"
                                    >
                                        <span className="sr-only">
                                            Time of day
                                        </span>
                                    </th>
                                    {DAY_INDEXES.map((dayIndex) => (
                                        <th
                                            key={dayIndex}
                                            scope="col"
                                            className="text-muted-foreground p-1 text-center text-xs font-semibold"
                                        >
                                            {dayLabel(
                                                dayIndex,
                                                workWeekStartDay,
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {DAY_PARTS.map((part) => (
                                    <tr key={part.key}>
                                        <th
                                            scope="row"
                                            className="text-muted-foreground p-1 text-left text-xs font-medium whitespace-nowrap"
                                        >
                                            {part.label}
                                            <span className="block font-normal">
                                                {part.timeLabel}
                                            </span>
                                        </th>
                                        {DAY_INDEXES.map((dayIndex) => {
                                            const cell = cellAt(
                                                row,
                                                dayIndex,
                                                part.key,
                                            );
                                            return (
                                                <td
                                                    key={dayIndex}
                                                    className="p-0"
                                                >
                                                    <div
                                                        className={cn(
                                                            'flex min-h-9 items-center justify-center rounded-md px-1 text-center text-[10px] font-medium',
                                                            CELL_STATE_CLASSES[
                                                                cell.state
                                                            ],
                                                            cell.state ===
                                                                'available' &&
                                                                'text-white',
                                                            cell.state ===
                                                                'unknown' &&
                                                                'text-muted-foreground',
                                                        )}
                                                    >
                                                        {
                                                            CELL_STATE_LABELS[
                                                                cell.state
                                                            ]
                                                        }
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-muted-foreground text-sm">
                        {row?.has_availability_data
                            ? 'No availability information applies to this week.'
                            : 'This caregiver has not shared availability yet.'}
                    </p>
                )}
            </DialogContent>
        </Dialog>
    );
}
